import React, { useState } from "react";
import {
  FaInstagram,
  FaFacebookF,
  FaWhatsapp,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";

const ContactForm = () => {
  const COLORS = {
    bgPrimary: "#080808",
    bgSecondary: "#121212",
    accent: "#C6A75E",
    accentHover: "#D4B97A",
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "", // Added phone field
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 1. NAME VALIDATION: Only allow alphabets and spaces
    if (name === "name") {
      const alphabetOnly = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({ ...formData, [name]: alphabetOnly });
      return;
    }

    // 2. PHONE VALIDATION: Only allow numbers and max 10 digits
    if (name === "phone") {
      const numbersOnly = value.replace(/[^0-9]/g, "");
      if (numbersOnly.length <= 10) {
        setFormData({ ...formData, [name]: numbersOnly });
      }
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, phone, subject, message } = formData;

    // Final check for 10 digits
    if (phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    const whatsappMessage = `New Contact Form Submission:%0AName: ${name}%0APhone: ${phone}%0AEmail: ${email}%0ASubject: ${subject}%0AMessage: ${message}`;
    const adminNumber = "919818104905";

    window.open(`https://wa.me/${adminNumber}?text=${whatsappMessage}`, "_blank");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  return (
    <section style={{ backgroundColor: COLORS.bgPrimary }} className="relative py-20 px-6 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C6A75E]/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16 text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
            <div className="h-[1px] w-12 bg-[#C6A75E]" />
            <span className="text-[#C6A75E] uppercase tracking-[0.3em] text-xs font-bold">Secure Legal Counsel</span>
          </div>
          <h2 className="text-4xl md:text-6xl text-white font-serif font-medium leading-tight">
            Get in <span className="italic text-[#C6A75E]">Touch</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          {/* INFO SECTION */}
          <div className="lg:col-span-5 space-y-6">
            <div style={{ backgroundColor: COLORS.bgSecondary }} className="p-8 rounded-3xl border border-white/5 shadow-2xl h-full flex flex-col justify-between">
              <div className="space-y-10">
                <div className="flex gap-6 group">
                  <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#C6A75E]/50 transition-colors">
                    <FaMapMarkerAlt className="text-[#C6A75E] text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1 italic">Office Chamber</h4>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-[250px]">Chamber no. 469, civil court Compound, Raj Nagar, Ghaziabad</p>
                  </div>
                </div>
                <div className="flex gap-6 group">
                  <div className="w-14 h-14 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#C6A75E]/50 transition-colors">
                    <FaEnvelope className="text-[#C6A75E] text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1 italic">Direct Inquiry</h4>
                    <p className="text-[#C6A75E] font-bold text-xl">+91 84472 20987</p>
                  </div>
                </div>
              </div>
              <div className="mt-12 space-y-6">
                <a href="https://wa.me/918447220987" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 p-4 rounded-2xl border border-[#C6A75E]/20 bg-[#C6A75E]/5 hover:bg-[#C6A75E] hover:text-black transition-all duration-300">
                  <FaWhatsapp className="text-xl" />
                  <span className="font-bold tracking-wide">CHAT WITH AN EXPERT</span>
                </a>
              </div>
            </div>
          </div>

          {/* FORM SECTION */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="bg-white/[0.02] backdrop-blur-md p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Name Input */}
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#C6A75E] font-bold">Full Name (Alphabets only)</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#C6A75E]/50 transition-all"
                    placeholder="Enter Name"
                  />
                </div>
                {/* Phone Input */}
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#C6A75E] font-bold">Phone (10 Digits)</label>
                  <input
                    type="text"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#C6A75E]/50 transition-all"
                    placeholder="98XXXXXXXX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-[#C6A75E] font-bold">Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#C6A75E]/50 transition-all"
                  placeholder="email@example.com"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-[#C6A75E] font-bold">Message</label>
                <textarea
                  name="message"
                  rows="4"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#C6A75E]/50 transition-all resize-none"
                  placeholder="Describe your legal requirements..."
                ></textarea>
              </div>

              <button type="submit" className="w-full mt-4 py-4 rounded-xl font-bold uppercase tracking-widest text-sm bg-gradient-to-r from-[#8B5E3C] to-[#C6A75E] text-black hover:opacity-90 active:scale-[0.98] transition-all">
                Submit Consultation Request
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;